import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DieselLayout } from '../../components/diesel/DieselLayout';
import { useDiesel } from '../../context/DieselContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
    Truck,
    Users,
    Fuel,
    AlertTriangle,
    TrendingUp,
    TrendingDown,
    Gauge,
    Clock,
    CheckCircle,
    ArrowRight,
    BarChart3,
    RefreshCw,
} from 'lucide-react';

export default function DieselDashboardPage() {
    const { hasPermission, nombreSistema } = useDiesel();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);

    useEffect(() => {
        loadDashboard();
    }, []);

    const loadDashboard = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/diesel/dashboard`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            
            if (res.ok) {
                const data = await res.json();
                setStats(data);
            } else {
                // Fallback a datos vacíos
                setStats({
                    vehiculos_activos: 0,
                    conductores_activos: 0,
                    cargas_hoy: 0,
                    cargas_semana: 0,
                    alertas_pendientes: 0,
                    litros_mes: 0,
                    rendimiento_promedio: 0,
                    km_recorridos_mes: 0,
                });
            }
        } catch (err) {
            console.error('Error loading dashboard:', err);
            toast.error('Error al cargar el dashboard');
        } finally {
            setLoading(false);
        }
    };

    return (
        <DieselLayout>
            <div className="space-y-6 animate-fade-in">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight">
                            Dashboard
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Bienvenido al panel de control de {nombreSistema}
                        </p>
                    </div>
                    <div className="flex gap-2">
                        {hasPermission('registrar_cargas') && (
                            <Link to="/diesel/cargas/nueva">
                                <Button 
                                    className="bg-[var(--diesel-primary)] text-[var(--diesel-secondary)] hover:bg-[var(--diesel-primary)]/90"
                                >
                                    <Fuel className="h-4 w-4 mr-2" />
                                    Nueva Carga
                                </Button>
                            </Link>
                        )}
                        <Button variant="outline" onClick={loadDashboard}>
                            <RefreshCw className="h-4 w-4" />
                        </Button>
                    </div>
                </div>

                {/* Mensaje de bienvenida para nuevas empresas */}
                {!loading && stats?.vehiculos_activos === 0 && (
                    <Card className="border-2 border-[var(--diesel-primary)] bg-[var(--diesel-primary)]/5">
                        <CardContent className="p-6">
                            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                                <div className="p-4 bg-[var(--diesel-primary)]/20 rounded-xl">
                                    <Fuel className="h-10 w-10" style={{ color: 'var(--diesel-primary)' }} />
                                </div>
                                <div className="flex-1">
                                    <h2 className="text-xl font-bold mb-1">¡Bienvenido a {nombreSistema}!</h2>
                                    <p className="text-muted-foreground mb-4">
                                        Para comenzar, configura los modelos de vehículos y registra tu flota.
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        <Link to="/diesel/configuracion">
                                            <Button variant="outline" size="sm">
                                                1. Configurar Modelos
                                                <ArrowRight className="h-4 w-4 ml-1" />
                                            </Button>
                                        </Link>
                                        <Link to="/diesel/vehiculos">
                                            <Button variant="outline" size="sm">
                                                2. Registrar Vehículos
                                                <ArrowRight className="h-4 w-4 ml-1" />
                                            </Button>
                                        </Link>
                                        <Link to="/diesel/conductores">
                                            <Button variant="outline" size="sm">
                                                3. Agregar Conductores
                                                <ArrowRight className="h-4 w-4 ml-1" />
                                            </Button>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="border-2">
                        <CardContent className="p-4">
                            {loading ? (
                                <Skeleton className="h-20" />
                            ) : (
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs uppercase tracking-wider text-muted-foreground">
                                            Vehículos
                                        </p>
                                        <p className="text-3xl font-black mt-1">{stats?.vehiculos_activos}</p>
                                        <p className="text-xs text-muted-foreground">activos</p>
                                    </div>
                                    <div className="p-3 bg-blue-500/10 rounded-lg">
                                        <Truck className="h-6 w-6 text-blue-600" />
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card className="border-2">
                        <CardContent className="p-4">
                            {loading ? (
                                <Skeleton className="h-20" />
                            ) : (
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs uppercase tracking-wider text-muted-foreground">
                                            Conductores
                                        </p>
                                        <p className="text-3xl font-black mt-1">{stats?.conductores_activos}</p>
                                        <p className="text-xs text-muted-foreground">registrados</p>
                                    </div>
                                    <div className="p-3 bg-emerald-500/10 rounded-lg">
                                        <Users className="h-6 w-6 text-emerald-600" />
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card className="border-2">
                        <CardContent className="p-4">
                            {loading ? (
                                <Skeleton className="h-20" />
                            ) : (
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs uppercase tracking-wider text-muted-foreground">
                                            Cargas Hoy
                                        </p>
                                        <p className="text-3xl font-black mt-1">{stats?.cargas_hoy}</p>
                                        <p className="text-xs text-muted-foreground">registradas</p>
                                    </div>
                                    <div className="p-3 bg-amber-500/10 rounded-lg">
                                        <Fuel className="h-6 w-6 text-amber-600" />
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card className="border-2 border-destructive/30">
                        <CardContent className="p-4">
                            {loading ? (
                                <Skeleton className="h-20" />
                            ) : (
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs uppercase tracking-wider text-muted-foreground">
                                            Alertas
                                        </p>
                                        <p className="text-3xl font-black mt-1 text-destructive">
                                            {stats?.alertas_pendientes}
                                        </p>
                                        <p className="text-xs text-muted-foreground">pendientes</p>
                                    </div>
                                    <div className="p-3 bg-destructive/10 rounded-lg">
                                        <AlertTriangle className="h-6 w-6 text-destructive" />
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Segunda fila de métricas */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="border-2">
                        <CardContent className="p-6">
                            {loading ? (
                                <Skeleton className="h-24" />
                            ) : (
                                <div className="flex items-center gap-4">
                                    <div className="p-4 bg-[var(--diesel-primary)]/10 rounded-xl">
                                        <Gauge className="h-8 w-8" style={{ color: 'var(--diesel-primary)' }} />
                                    </div>
                                    <div>
                                        <p className="text-xs uppercase tracking-wider text-muted-foreground">
                                            Rendimiento Promedio
                                        </p>
                                        <p className="text-3xl font-black" style={{ color: 'var(--diesel-primary)' }}>
                                            {stats?.rendimiento_promedio?.toFixed(1) || '0.0'} <span className="text-lg">km/l</span>
                                        </p>
                                        <p className="text-xs text-muted-foreground">Flota completa</p>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card className="border-2">
                        <CardContent className="p-6">
                            {loading ? (
                                <Skeleton className="h-24" />
                            ) : (
                                <div className="flex items-center gap-4">
                                    <div className="p-4 bg-blue-500/10 rounded-xl">
                                        <Fuel className="h-8 w-8 text-blue-600" />
                                    </div>
                                    <div>
                                        <p className="text-xs uppercase tracking-wider text-muted-foreground">
                                            Litros Este Mes
                                        </p>
                                        <p className="text-3xl font-black text-blue-600">
                                            {stats?.litros_mes?.toLocaleString() || '0'} <span className="text-lg">L</span>
                                        </p>
                                        <p className="text-xs text-muted-foreground">Consumo total</p>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card className="border-2">
                        <CardContent className="p-6">
                            {loading ? (
                                <Skeleton className="h-24" />
                            ) : (
                                <div className="flex items-center gap-4">
                                    <div className="p-4 bg-emerald-500/10 rounded-xl">
                                        <TrendingUp className="h-8 w-8 text-emerald-600" />
                                    </div>
                                    <div>
                                        <p className="text-xs uppercase tracking-wider text-muted-foreground">
                                            KM Recorridos
                                        </p>
                                        <p className="text-3xl font-black text-emerald-600">
                                            {stats?.km_recorridos_mes?.toLocaleString() || '0'} <span className="text-lg">km</span>
                                        </p>
                                        <p className="text-xs text-muted-foreground">Este mes</p>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Accesos rápidos */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {hasPermission('registrar_cargas') && (
                        <Link to="/diesel/cargas/nueva" className="block">
                            <Card className="border-2 hover:border-[var(--diesel-primary)] transition-colors cursor-pointer h-full">
                                <CardContent className="p-6 flex flex-col items-center justify-center text-center">
                                    <div className="p-4 bg-[var(--diesel-primary)]/10 rounded-xl mb-3">
                                        <Fuel className="h-8 w-8" style={{ color: 'var(--diesel-primary)' }} />
                                    </div>
                                    <p className="font-bold">Nueva Carga</p>
                                    <p className="text-xs text-muted-foreground">Registrar combustible</p>
                                </CardContent>
                            </Card>
                        </Link>
                    )}

                    {hasPermission('ver_vehiculos') && (
                        <Link to="/diesel/vehiculos" className="block">
                            <Card className="border-2 hover:border-[var(--diesel-primary)] transition-colors cursor-pointer h-full">
                                <CardContent className="p-6 flex flex-col items-center justify-center text-center">
                                    <div className="p-4 bg-blue-500/10 rounded-xl mb-3">
                                        <Truck className="h-8 w-8 text-blue-600" />
                                    </div>
                                    <p className="font-bold">Vehículos</p>
                                    <p className="text-xs text-muted-foreground">Gestionar flota</p>
                                </CardContent>
                            </Card>
                        </Link>
                    )}

                    {hasPermission('ver_alertas') && (
                        <Link to="/diesel/alertas" className="block">
                            <Card className="border-2 hover:border-[var(--diesel-primary)] transition-colors cursor-pointer h-full">
                                <CardContent className="p-6 flex flex-col items-center justify-center text-center">
                                    <div className="p-4 bg-amber-500/10 rounded-xl mb-3">
                                        <AlertTriangle className="h-8 w-8 text-amber-600" />
                                    </div>
                                    <p className="font-bold">Alertas</p>
                                    <p className="text-xs text-muted-foreground">Ver notificaciones</p>
                                </CardContent>
                            </Card>
                        </Link>
                    )}

                    {hasPermission('ver_reportes') && (
                        <Link to="/diesel/reportes" className="block">
                            <Card className="border-2 hover:border-[var(--diesel-primary)] transition-colors cursor-pointer h-full">
                                <CardContent className="p-6 flex flex-col items-center justify-center text-center">
                                    <div className="p-4 bg-purple-500/10 rounded-xl mb-3">
                                        <BarChart3 className="h-8 w-8 text-purple-600" />
                                    </div>
                                    <p className="font-bold">Reportes</p>
                                    <p className="text-xs text-muted-foreground">Análisis y exportar</p>
                                </CardContent>
                            </Card>
                        </Link>
                    )}
                </div>
            </div>
        </DieselLayout>
    );
}
